/// <amd-module name="Test/TestPlansHubRefresh/TestCaseResultsPanel/Utils" />
import { IResultsPanelTableItem } from "Test/TestPlansHubRefresh/TestCaseResultsPanel/Redux/TestCaseResultsPanelContracts";
import * as Resources from "Test/TestPlansHubRefresh/TestCaseResultsPanel/Resources";
import { TestCaseAssociatedResult, TestCaseResultsData, UserFriendlyTestOutcome } from "TestPlanCommon/Contracts/TestPlan";
import { IVssPageContext } from "VSS/Platform/Context";
import * as VS_Services from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { ISimpleListCell } from "VSSUI/List";
import { ago, AgoFormat } from "VSSUI/Utilities/Date";

/**
 * Returns the data provider content in an async manner
 * @param dataProviderContext The context to be passed to the data provider to pass the required values, which would otherwise be used from the request context.
 */
export async function getDataProviderContentForTestResultsAsync(
    pageContext: IVssPageContext,
    dataProviderContext: { [key: string]: any }
): Promise<TestCaseResultsData | undefined> {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const data = contributionService.getDataAsync<TestCaseResultsData>(
        "ms.vss-test-web.testcase-results-data-provider",
        dataProviderContext,
        true
    );
    return data;
}

export function getTestCaseResultsForTable(testCaseResults: TestCaseAssociatedResult[]): IResultsPanelTableItem[] {
    var testCaseResultItems: IResultsPanelTableItem[] = [];

    // Return if there are no test cases
    if (!testCaseResults || testCaseResults.length == 0) {
        return testCaseResultItems;
    }

    for (let i = 0; i < testCaseResults.length; i++) {
        const outcome = getOutcomeDetails(testCaseResults[i].outcome);

        // When results get reset, they won't be shown, same as the current experience.
        if (outcome.text !== Resources.Active) {
            const testPointTableItem: IResultsPanelTableItem = {
                outcome: outcome,
                timestamp: ago(testCaseResults[i].completedDate, AgoFormat.Compact),
                configuration: (testCaseResults[i].configuration && testCaseResults[i].configuration.name) ?
                    testCaseResults[i].configuration.name : Resources.Deleted,
                tester: getDisplayName(testCaseResults[i].tester),
                runBy: getDisplayName(testCaseResults[i].runBy),
                testPlanName: (testCaseResults[i].plan && testCaseResults[i].plan.name) ?
                    testCaseResults[i].plan.name : Resources.Deleted,
                testSuiteName: (testCaseResults[i].suite && testCaseResults[i].suite.name) ?
                    testCaseResults[i].suite.name : Resources.Deleted,
                pointId: testCaseResults[i].pointId,
                runId: testCaseResults[i].runId,
                resultId: testCaseResults[i].resultId
            };

            testCaseResultItems.push(testPointTableItem);
        }
    }

    return testCaseResultItems;
}

// gets the appropriate icon, text and class name for the outcome type.
function getOutcomeDetails(pointOutcome: UserFriendlyTestOutcome): ISimpleListCell {
    let details: ISimpleListCell = {} as ISimpleListCell;

    if (pointOutcome === UserFriendlyTestOutcome.Passed) {
        details = { iconProps: { iconName: "CompletedSolid", className: "pass-icon" }, text: Resources.Passed };
    } else if (pointOutcome === UserFriendlyTestOutcome.Failed) {
        details = { iconProps: { iconName: "StatusErrorFull", className: "fail-icon" }, text: Resources.Failed };
    } else if (pointOutcome === UserFriendlyTestOutcome.Blocked) {
        details = { iconProps: { iconName: "Blocked2Solid", className: "block-icon" }, text: Resources.Blocked };
    } else if (pointOutcome === UserFriendlyTestOutcome.NotApplicable) {
        details = { iconProps: { iconName: "BlockedSolid", className: "not-applicable-icon" }, text: Resources.NotApplicable };
    } else if (pointOutcome === UserFriendlyTestOutcome.Paused) {
        details = { iconProps: { iconName: "CirclePauseSolid", className: "paused-icon" }, text: Resources.Paused };
    } else if (pointOutcome === UserFriendlyTestOutcome.InProgress) {
        details = { iconProps: { iconName: "AwayStatus", className: "in-progress-icon" }, text: Resources.InProgress };
    } else if (pointOutcome === UserFriendlyTestOutcome.Timeout) {
        details = { iconProps: { iconName: "Clock", className: "timed-out-icon" }, text: Resources.Timeout };
    } else if (pointOutcome === UserFriendlyTestOutcome.Warning) {
        details = { iconProps: { iconName: "Warning", className: "warning-icon" }, text: Resources.Warning };
    } else if (pointOutcome === UserFriendlyTestOutcome.Error) {
        details = { iconProps: { iconName: "StatusErrorFull", className: "error-icon" }, text: Resources.Error };
    } else if (pointOutcome === UserFriendlyTestOutcome.NotExecuted) {
        details = { iconProps: { iconName: "NotExecuted", className: "not-executed-icon" }, text: Resources.NotExecuted };
    } else if (pointOutcome === UserFriendlyTestOutcome.Inconclusive) {
        details = { iconProps: { iconName: "BlockedSolid", className: "inconclusive-icon" }, text: Resources.Inconclusive };
    } else if (pointOutcome === UserFriendlyTestOutcome.Aborted) {
        details = { iconProps: { iconName: "CircleStopSolid", className: "aborted-icon" }, text: Resources.Aborted };
    } else if (pointOutcome === UserFriendlyTestOutcome.None) {
        details = { iconProps: { iconName: "", className: "none-icon" }, text: Resources.None };
    } else if (pointOutcome === UserFriendlyTestOutcome.Unspecified) {
        details = { iconProps: { iconName: "", className: "unspecified-icon" }, text: Resources.Unspecified };
    } else if (pointOutcome === UserFriendlyTestOutcome.NotImpacted) {
        details = { iconProps: { iconName: "", className: "not-impacted-icon" }, text: Resources.NotImpacted };
    } else {
        details = { iconProps: { iconName: "CircleFill", className: "active-icon" }, text: Resources.Active };
    }
    return details;
}

function getDisplayName(identity: VS_Services.IdentityRef) {
    if (identity && identity.displayName) {
        return identity.displayName;
    }
    return Resources.Unassigned;
}
